import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { get, isEqual, omit } from 'lodash';

import {
  Grid,
  Col,
  Row
} from 'react-bootstrap';
import ComponentHeader from '../../components/Headers/component-header';
import { truncateText } from '../../utils/shared';
import InlineQuery from '../../components/InlineQuery';
import Table from '../../components/Table';
import showsConfig from '../configs/shows-config';
import { extractFilterValues } from "../../utils/filters";
import ShowInsights from "./ShowInsights";
import TabsButtonGroup from '../../components/TabsButtonGroup';

import './styles/ShowAffinity.css';

import {
  anticipationAffinityColumns,
  anticipationAffinityDefaultSorts, audienceAffinityColumns, audienceAffinityDefaultSorts
} from './table-config';
import { FilterTypes } from "../../components/Filters";

const ShowAffinity = ({
  queries: {
    anticipationFollowerAffinity,
    showReportAffinityOR,
  },
  queryActions: { runFormattedInlineQueriesForPage },
  show,
  filters,
  blocked,
  updateEpisodeOptionsLoader,
  hasShowAired
}) => {
  const showTitle = get(show, 'name', '');
  const [selectedFilters, setSelectedFilters] = useState([])
  const filtersFields = filters && filters.fields;

  const renderFollowerAffinity = () => {
    return (
      <Row>
        <Col xs={12}>
          <ComponentHeader
            title={
              <span>
                    People Who Follow{' '}
                <em>{truncateText(get(show, 'name', 'this show'), 80)}</em>{' '}
                Also Watch...
                  </span>
            }
          />
          <InlineQuery useChildForLoading query={anticipationFollowerAffinity}>
            {query => (
              <Table
                data={query.data}
                headerFormatter={anticipationAffinityColumns}
                defaultSorts={anticipationAffinityDefaultSorts}
                view="anticipationFollowerAffinity"
                filters={filtersFields}
                showRank={false}
                queryMeta={
                  showsConfig.SHOW_REPORT_FILTERS.showReportAffinity.queries
                    .anticipationFollowerAffinity
                }
                titleName={showTitle}
              />
            )}
          </InlineQuery>
        </Col>
      </Row>
    );
  };

  const renderWatchAffinity = () => {
    return (
      <Row>
        <Col xs={12}>
          <ComponentHeader
            title={
              <span>
                People Who Watch {' '}
                <em>{truncateText(showTitle, 80)}</em> {' '}
                Also Watch...
              </span>
            }
          />
          <InlineQuery useChildForLoading query={showReportAffinityOR}>
            {query => (
              <Table
                data={query.data}
                headerFormatter={audienceAffinityColumns}
                defaultSorts={audienceAffinityDefaultSorts}
                view="showReportAffinityOR"
                filters={filtersFields}
                showRank={false}
                queryMeta={
                  showsConfig.SHOW_REPORT_FILTERS.showReportAffinity.queries
                    .showReportAffinityOR
                }
                titleName={show.name}
              />
            )}
          </InlineQuery>
        </Col>
      </Row>
    )
  };

  useEffect(() => {
    const updateQueries = () => {
      const filterOverrides = {};

      filterOverrides.ageDemo = {id: 'ageDemo', value: {value: 'age'}};
      filterOverrides.genderDemo = {
        id: 'genderDemo',
        value: {value: 'gender'}
      };
      filterOverrides.countryDemo = {
        id: 'countryDemo',
        value: {value: 'country'}
      };
      filterOverrides.contentType = {
        id: 'contentType',
        value: {value: 'series'}
      }

      const pageConfigs = showsConfig.SHOW_REPORT_FILTERS[showsConfig.SHOW_AFFINITY_PAGE_ID];
      const queries = pageConfigs.queries;
      const pageQueries = hasShowAired
        ? queries
        : omit({...queries}, ['showReportAffinityOR'])
      const configs = { ...pageConfigs, ...{ queries: pageQueries}};

      runFormattedInlineQueriesForPage(
        configs,
        {
          ...filtersFields,
          ...filterOverrides
        }
      );
    };

    if (blocked) {
      return;
    }
    const values = extractFilterValues(filtersFields);
    const hasNewValues = !isEqual(values, selectedFilters);
    if (hasNewValues) {
      updateEpisodeOptionsLoader(get(filtersFields, 'season.value', []));
      setSelectedFilters(values);
      updateQueries();
    }
  }, [
    blocked,
    filtersFields,
    hasShowAired,
    updateEpisodeOptionsLoader,
    selectedFilters,
    runFormattedInlineQueriesForPage
  ]);

  return (
    <>
      <ComponentHeader title={"Affinity"}/>
      <Grid fluid componentClass="section" className={"show-affinity"}>
        { hasShowAired && (
          <TabsButtonGroup>
            <div icon={"VIEWER TO VIEWER"}>
              { renderWatchAffinity() }
            </div>
            <div icon={"FOLLOWER TO VIEWER"}>
              { renderFollowerAffinity() }
            </div>
          </TabsButtonGroup>
        )}
        { !hasShowAired && (
          renderFollowerAffinity()
        )}
      </Grid>
    </>
  );
}

export default ShowAffinity;

ShowInsights.propTypes = {
  queries: PropTypes.shape({
    anticipationFollowerAffinity: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape({})),
      loading: PropTypes.bool
    }).isRequired,
    showReportAffinityOR: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape({})),
      loading: PropTypes.bool
    }).isRequired,
  }).isRequired,
  show: PropTypes.shape({
    name: PropTypes.string.isRequired
  }).isRequired,
  updateEpisodeOptionsLoader: PropTypes.func.isRequired,
  blocked: PropTypes.bool.isRequired,
  queryActions: PropTypes.shape({
    runFormattedInlineQueriesForPage: PropTypes.func.isRequired
  }).isRequired,
  filters: PropTypes.shape({
    fields: FilterTypes.FilterSetType, // eslint-disable-line react/no-typos
    groupings: FilterTypes.GroupingSetType, // eslint-disable-line react/no-typos
    options: PropTypes.shape({})
  }).isRequired,
  hasShowAired: PropTypes.bool
}
